import { PasswordChangeType } from "@/utils/global-constants";
import { cryptoUtil } from "@/utils/crypto/crypto";

export const COOKIE_KEYS = {
  PASSWORD_CHANGE_REQUIRED: "password-change-required",
  PASSWORD_CHANGE_TYPE: "password-change-type",
  PASSWORD_CHANGE_COMPLETED: "password-change-completed",
  USER_ROLE: "user-role",
} as const;

const MOCK_USER_ROLE_PREFIX = "mock:";

export function setCookie(key: string, value: string, maxAge = 60 * 60 * 24): void {
  if (typeof document === "undefined") return;
  document.cookie = `${key}=${encodeURIComponent(value)}; path=/; max-age=${maxAge}`;
}

export function deleteCookie(key: string): void {
  if (typeof document === "undefined") return;
  document.cookie = `${key}=; path=/; max-age=0`;
}

function getCookie(key: string): string | null {
  if (typeof document === "undefined") return null;
  const cookie = document.cookie
    .split(";")
    .map((value) => value.trim())
    .find((value) => value.startsWith(`${key}=`));
  if (!cookie) return null;
  return decodeURIComponent(cookie.slice(key.length + 1));
}

function secretKey(): string | null {
  return import.meta.env.VITE_SECRET_KEY || null;
}

function canUseMockRoleCookieFallback(): boolean {
  return import.meta.env.MODE === "mock";
}

export function setPasswordChangeRequired(type: PasswordChangeType): void {
  setCookie(COOKIE_KEYS.PASSWORD_CHANGE_REQUIRED, "true");
  setCookie(COOKIE_KEYS.PASSWORD_CHANGE_TYPE, type);
  setCookie(COOKIE_KEYS.PASSWORD_CHANGE_COMPLETED, "false");
}

export function setPasswordChangeCompleted(): void {
  setCookie(COOKIE_KEYS.PASSWORD_CHANGE_COMPLETED, "true");
  deleteCookie(COOKIE_KEYS.PASSWORD_CHANGE_REQUIRED);
  deleteCookie(COOKIE_KEYS.PASSWORD_CHANGE_TYPE);
}

export function clearPasswordChangeCookies(): void {
  deleteCookie(COOKIE_KEYS.PASSWORD_CHANGE_REQUIRED);
  deleteCookie(COOKIE_KEYS.PASSWORD_CHANGE_TYPE);
  deleteCookie(COOKIE_KEYS.PASSWORD_CHANGE_COMPLETED);
}

export function getPasswordChangeType(): PasswordChangeType | null {
  const value = getCookie(COOKIE_KEYS.PASSWORD_CHANGE_TYPE);
  return value === PasswordChangeType.INITIAL || value === PasswordChangeType.EXPIRED
    ? value
    : null;
}

export async function setUserRoleCookie(role: string): Promise<void> {
  if (!role) return;

  const key = secretKey();
  if (!key) {
    if (canUseMockRoleCookieFallback()) {
      setCookie(COOKIE_KEYS.USER_ROLE, `${MOCK_USER_ROLE_PREFIX}${role}`);
    }
    return;
  }

  try {
    const encryptedRole = await cryptoUtil.encrypt(role, key);
    setCookie(COOKIE_KEYS.USER_ROLE, encryptedRole);
  } catch (error) {
    if (canUseMockRoleCookieFallback()) {
      setCookie(COOKIE_KEYS.USER_ROLE, `${MOCK_USER_ROLE_PREFIX}${role}`);
      return;
    }
    throw error;
  }
}

export async function getUserRoleFromCookie(): Promise<string | null> {
  const roleCookie = getCookie(COOKIE_KEYS.USER_ROLE);
  const key = secretKey();
  if (!roleCookie) return null;

  if (roleCookie.startsWith(MOCK_USER_ROLE_PREFIX)) {
    return canUseMockRoleCookieFallback()
      ? roleCookie.slice(MOCK_USER_ROLE_PREFIX.length)
      : null;
  }

  if (!key) return null;

  try {
    return await cryptoUtil.decrypt(roleCookie, key);
  } catch {
    return null;
  }
}

export function clearUserRoleCookie(): void {
  deleteCookie(COOKIE_KEYS.USER_ROLE);
}
